import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { fmt, fmtDate } from "../lib/utils";
import { useFormDraft } from "../lib/formDraft";
import {
  Button, Input, Select, FormField, Dialog, DialogCancel,
  Badge, Spinner, Empty, PageHeader, SearchInput,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
  Card, CardContent,
} from "../components/ui";
import { Plus, Trash2, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import { toast } from "sonner";

const ESTADO_FAC = {
  PENDIENTE: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300",
  COBRADO:   "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300",
  ANULADA:   "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300",
};

// Devuelve clase + leyenda según vencimiento del cobro
function cobroUrgencia(fechaStr, estado) {
  if (estado !== "PENDIENTE" || !fechaStr) return null;
  const hoy  = new Date();
  hoy.setHours(0, 0, 0, 0);
  const fecha = new Date(fechaStr);
  fecha.setHours(0, 0, 0, 0);
  const dias = Math.round((fecha - hoy) / 86400000);
  if (dias < 0)  return { cls: "text-red-600 font-semibold",   icon: "overdue",  label: `Vencido hace ${Math.abs(dias)}d` };
  if (dias <= 5) return { cls: "text-amber-600 font-semibold", icon: "warning",  label: `En ${dias}d` };
  return null;
}

function emptyItem() { return { descripcion: "", cantidad: 1, precio_unitario: 0, tipo_iva: "IVA10" }; }
function emptyFacForm() { return { cliente_id: "", presupuesto_id: "", timbrado: "", forma_pago: "EFECTIVO", observaciones: "" }; }
function emptyAseg() { return { aseguradora_id: "", monto_aseguradora: 0, monto_cliente: 0, franquicia: 0 }; }

export default function Facturacion() {
  const [list,      setList]      = useState([]);
  const [clientes,  setClientes]  = useState([]);
  const [presups,   setPresups]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [open,      setOpen]      = useState(false);
  const [detail,    setDetail]    = useState(null);
  const [saving,    setSaving]    = useState(false);
  // Formulario
  const [form, setForm] = useState(emptyFacForm());
  const [items, setItems] = useState([emptyItem()]);
  const [aseg, setAseg] = useState(emptyAseg());
  const [aseguradoras, setAseguradoras] = useState([]);

  const resetForm = () => { setForm(emptyFacForm()); setItems([emptyItem()]); setAseg(emptyAseg()); };
  const formValues = { form, items, aseg };
  const draft = useFormDraft("factura", formValues, resetForm);

  function openNueva() {
    const d = draft.load();
    const f = { ...emptyFacForm(), ...d?.form };
    // El presupuesto pudo haber sido facturado o el cliente eliminado desde que se guardó el borrador
    if (f.presupuesto_id && !presups.some((p) => String(p.id) === String(f.presupuesto_id))) f.presupuesto_id = "";
    if (f.cliente_id && !clientes.some((c) => String(c.id) === String(f.cliente_id))) f.cliente_id = "";
    setForm(f);
    setItems(d?.items?.length ? d.items : [emptyItem()]);
    setAseg({ ...emptyAseg(), ...d?.aseg });
    setOpen(true);
  }

  const load = useCallback(async () => {
    const [f, c, p, a] = await Promise.all([
      api.get("/facturas"),
      api.get("/clientes"),
      api.get("/presupuestos", { params: { estado: "APROBADO" } }),
      api.get("/aseguradoras"),
    ]);
    setList(f.data);
    setClientes(c.data);
    setPresups(p.data);
    setAseguradoras(a.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Si se selecciona presupuesto, cargar sus ítems automáticamente
  async function onPresupSelect(pid) {
    s("presupuesto_id", pid);
    if (!pid) return;
    const pre = presups.find((p) => String(p.id) === String(pid));
    if (!pre) return;
    s("cliente_id", String(pre.cliente_id));
    const preItems = pre.items ?? [];
    setItems(preItems.map((i) => ({
      descripcion: i.descripcion,
      cantidad: i.cantidad,
      precio_unitario: i.precio_unitario,
      tipo_iva: "IVA10",
    })));
  }

  function updateItem(idx, field, val) {
    setItems((prev) => prev.map((it, j) =>
      j !== idx ? it : { ...it, [field]: ["cantidad","precio_unitario"].includes(field) ? parseFloat(val)||0 : val }
    ));
  }

  // Cálculos IVA paraguayo
  const exentas = items.reduce((s, i) => s + (i.tipo_iva === "EXENTA" ? i.cantidad * i.precio_unitario : 0), 0);
  const b5      = items.reduce((s, i) => s + (i.tipo_iva === "IVA5"   ? i.cantidad * i.precio_unitario : 0), 0);
  const b10     = items.reduce((s, i) => s + (i.tipo_iva === "IVA10"  ? i.cantidad * i.precio_unitario : 0), 0);
  const iva5    = b5 * 5 / 105;
  const iva10   = b10 * 10 / 110;
  const totalIva = iva5 + iva10;
  const total    = exentas + b5 + b10;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.cliente_id) { toast.error("Seleccioná un cliente"); return; }
    setSaving(true);
    try {
      await api.post("/facturas", {
        cliente_id: parseInt(form.cliente_id),
        presupuesto_id: form.presupuesto_id ? parseInt(form.presupuesto_id) : null,
        timbrado: form.timbrado || null,
        forma_pago: form.forma_pago,
        observaciones: form.observaciones || null,
        aseguradora_id: aseg.aseguradora_id ? parseInt(aseg.aseguradora_id) : null,
        monto_aseguradora: aseg.monto_aseguradora,
        monto_cliente: aseg.monto_cliente,
        franquicia: aseg.franquicia,
        items: items.map((i) => ({
          descripcion: i.descripcion,
          cantidad: i.cantidad,
          precio_unitario: i.precio_unitario,
          tipo_iva: i.tipo_iva,
        })),
      });
      toast.success("Factura creada");
      draft.clear();
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error");
    } finally {
      setSaving(false);
    }
  }

  async function pagar(id) {
    try {
      await api.patch(`/facturas/${id}/pagar`);
      toast.success("Cobro registrado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al registrar cobro");
    }
  }

  async function anular(id) {
    if (!window.confirm("\u00bfAnular esta factura?")) return;
    try {
      await api.patch(`/facturas/${id}/anular`);
      toast.success("Factura anulada");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al anular");
    }
  }

  const filtered = list.filter((f) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return f.numero?.toLowerCase().includes(q) || f.cliente_nombre?.toLowerCase().includes(q);
  });

  if (loading) return <div className="flex items-center justify-center h-full"><Spinner className="w-8 h-8" /></div>;

  return (
    <div className="p-4 sm:p-6">
      <PageHeader title="Facturación / Cobros" sub={`${list.length} registros`}>
        <Button onClick={openNueva}>
          <Plus className="w-4 h-4" /> Nueva factura
        </Button>
      </PageHeader>

      <SearchInput value={search} onChange={setSearch} placeholder="Buscar…" className="w-full sm:w-64 mb-4" />

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>N° / Pres.</TableHead>
            <TableHead>Ingreso vehículo</TableHead>
            <TableHead>Cliente</TableHead>
            <TableHead>Timbrado</TableHead>
            <TableHead>Cobro estimado</TableHead>
            <TableHead className="text-right">Total</TableHead>
            <TableHead>Forma pago</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.length === 0 && (
            <TableRow><TableCell colSpan={8}><Empty message="Sin facturas" /></TableCell></TableRow>
          )}
          {filtered.map((f) => (
            <TableRow key={f.id}>
              <TableCell className="font-mono text-xs">
                {f.numero}
                {f.origen === "AUTO" && <span className="ml-1 text-[10px] text-muted-foreground">(auto)</span>}
              </TableCell>
              <TableCell className="text-sm">{f.vehiculo_fecha_ingreso ? fmtDate(f.vehiculo_fecha_ingreso) : fmtDate(f.fecha)}</TableCell>
              <TableCell className="text-sm">{f.cliente_nombre}</TableCell>
              <TableCell className="font-mono text-xs">{f.timbrado ?? "—"}</TableCell>
              <TableCell className="text-xs">
                {(() => {
                  const urg = cobroUrgencia(f.fecha_estimada_pago, f.estado);
                  if (!f.fecha_estimada_pago) return <span className="text-muted-foreground">—</span>;
                  return (
                    <span className={urg ? urg.cls : ""}>
                      {urg?.icon === "overdue" && <AlertCircle className="inline w-3 h-3 mr-0.5 -mt-0.5" />}
                      {urg?.icon === "warning" && <Clock className="inline w-3 h-3 mr-0.5 -mt-0.5" />}
                      {fmtDate(f.fecha_estimada_pago)}
                      {urg && <span className="ml-1 text-[10px]">({urg.label})</span>}
                    </span>
                  );
                })()}
              </TableCell>
              <TableCell className="text-right font-mono text-sm font-semibold">{fmt(f.total_general)}</TableCell>
              <TableCell className="text-xs">{f.forma_pago}</TableCell>
              <TableCell>
                <Badge className={ESTADO_FAC[f.estado]}>{f.estado}</Badge>
              </TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <button onClick={() => setDetail(f)} className="text-xs text-primary hover:underline">Ver</button>
                  {f.estado === "PENDIENTE" && (
                    <button onClick={() => pagar(f.id)} className="text-xs text-green-600 hover:underline ml-1">Cobrar</button>
                  )}
                  {f.estado !== "ANULADA" && (
                    <button onClick={() => anular(f.id)} className="text-xs text-destructive hover:underline ml-1">Anular</button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Dialog nueva factura */}
      <Dialog open={open} onClose={() => setOpen(false)} values={formValues} draft={draft} title="Nueva Factura" size="2xl">
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
            {/* Items */}
            <div className="flex-1 min-w-0 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormField label="Cliente *">
                  <Select value={form.cliente_id} onChange={(e) => s("cliente_id", e.target.value)} required>
                    <option value="">Seleccionar…</option>
                    {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </Select>
                </FormField>
                <FormField label="Desde presupuesto">
                  <Select value={form.presupuesto_id} onChange={(e) => onPresupSelect(e.target.value)}>
                    <option value="">Sin vinculación</option>
                    {presups.map((p) => <option key={p.id} value={p.id}>{p.numero} — {p.cliente_nombre}</option>)}
                  </Select>
                </FormField>
                <FormField label="Timbrado">
                  <Input value={form.timbrado} onChange={(e) => s("timbrado", e.target.value)} />
                </FormField>
                <FormField label="Forma de pago">
                  <Select value={form.forma_pago} onChange={(e) => s("forma_pago", e.target.value)}>
                    {["EFECTIVO","TRANSFERENCIA","TARJETA","CHEQUE","SEGURO"].map((p) => <option key={p}>{p}</option>)}
                  </Select>
                </FormField>
              </div>

              {/* Ítems */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold">Ítems</span>
                  <Button type="button" variant="outline" size="sm" onClick={() => setItems((i) => [...i, emptyItem()])}>
                    <Plus className="w-3.5 h-3.5" /> Agregar
                  </Button>
                </div>
                {items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 items-end mb-2">
                    <FormField label={idx === 0 ? "Descripción" : undefined}>
                      <Input value={item.descripcion} onChange={(e) => updateItem(idx, "descripcion", e.target.value)} required />
                    </FormField>
                    <FormField label={idx === 0 ? "Cant." : undefined}>
                      <Input type="number" min="0.01" step="0.01" className="w-20" value={item.cantidad} onChange={(e) => updateItem(idx,"cantidad",e.target.value)} />
                    </FormField>
                    <FormField label={idx === 0 ? "P. Unit" : undefined}>
                      <Input type="number" min="0" step="100" className="w-28" value={item.precio_unitario} onChange={(e) => updateItem(idx,"precio_unitario",e.target.value)} />
                    </FormField>
                    <FormField label={idx === 0 ? "IVA" : undefined}>
                      <Select value={item.tipo_iva} onChange={(e) => updateItem(idx,"tipo_iva",e.target.value)} className="w-24">
                        <option value="EXENTA">Exenta</option>
                        <option value="IVA5">5%</option>
                        <option value="IVA10">10%</option>
                      </Select>
                    </FormField>
                    <div className={idx === 0 ? "mt-5" : ""}>
                      <button type="button" onClick={() => setItems((i) => i.filter((_,j) => j!==idx))} disabled={items.length===1} className="p-2 text-muted-foreground hover:text-destructive disabled:opacity-30">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Resumen */}
            <div className="w-full lg:w-56 shrink-0">
              <Card className="sticky top-0">
                <CardContent className="p-4 space-y-2 text-sm">
                  <p className="font-semibold font-display mb-3">Totales IVA</p>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Exentas</span>
                    <span className="font-mono">{fmt(exentas)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Gravadas 5%</span>
                    <span className="font-mono">{fmt(b5)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Gravadas 10%</span>
                    <span className="font-mono">{fmt(b10)}</span>
                  </div>
                  <div className="flex justify-between border-t border-border pt-2">
                    <span className="text-muted-foreground">IVA total</span>
                    <span className="font-mono">{fmt(totalIva)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-base border-t border-border pt-2">
                    <span>TOTAL</span>
                    <span className="font-mono text-primary">{fmt(total)}</span>
                  </div>
                  <Button type="submit" loading={saving} className="w-full mt-3">Emitir factura</Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </Dialog>

      {/* Detalle */}
      {detail && (
        <FacturaDetail fac={detail} onClose={() => setDetail(null)} />
      )}
    </div>
  );
}

function FacturaDetail({ fac, onClose }) {
  const [full, setFull] = useState(fac);
  useEffect(() => {
    api.get(`/facturas/${fac.id}`).then((r) => setFull(r.data));
  }, [fac.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 h-full w-full sm:w-[480px] bg-card border-l border-border flex flex-col shadow-xl overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-card">
          <h2 className="font-semibold font-display">{full.numero}</h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-secondary text-muted-foreground">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><span className="text-muted-foreground">Cliente: </span>{full.cliente_nombre}</div>
            <div><span className="text-muted-foreground">Ingreso vehículo: </span>{fmtDate(full.vehiculo_fecha_ingreso ?? full.fecha)}</div>
            <div><span className="text-muted-foreground">Estado: </span><Badge className={ESTADO_FAC[full.estado]}>{full.estado}</Badge></div>
            <div><span className="text-muted-foreground">Forma pago: </span>{full.forma_pago}</div>
            {full.fecha_estimada_pago && (() => {
              const urg = cobroUrgencia(full.fecha_estimada_pago, full.estado);
              return (
                <div className="col-span-2">
                  <span className="text-muted-foreground">Cobro estimado: </span>
                  <span className={urg ? urg.cls : "font-medium"}>
                    {urg?.icon === "overdue" && <AlertCircle className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />}
                    {urg?.icon === "warning" && <Clock className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />}
                    {fmtDate(full.fecha_estimada_pago)}
                    {urg && <span className="ml-1.5 text-xs">({urg.label})</span>}
                  </span>
                </div>
              );
            })()}
            {full.aseguradora_nombre && (
              <div className="col-span-2"><span className="text-muted-foreground">Aseguradora: </span>{full.aseguradora_nombre}</div>
            )}
          </div>

          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-xs text-muted-foreground">
                <th className="text-left pb-1">Descripción</th>
                <th className="text-right pb-1">Cant</th>
                <th className="text-right pb-1">P.Unit</th>
                <th className="text-right pb-1">IVA</th>
                <th className="text-right pb-1">Total</th>
              </tr>
            </thead>
            <tbody>
              {(full.items ?? []).map((i) => (
                <tr key={i.id} className="border-b border-border/50">
                  <td className="py-1">{i.descripcion}</td>
                  <td className="text-right py-1 font-mono">{i.cantidad}</td>
                  <td className="text-right py-1 font-mono">{fmt(i.precio_unitario)}</td>
                  <td className="text-right py-1 text-xs text-muted-foreground">{i.tipo_iva}</td>
                  <td className="text-right py-1 font-mono">{fmt(i.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="text-sm space-y-1 text-right">
            <div className="flex justify-end gap-8">
              <span className="text-muted-foreground">Exentas</span><span className="font-mono">{fmt(full.total_exentas)}</span>
            </div>
            <div className="flex justify-end gap-8">
              <span className="text-muted-foreground">Gravadas 5%</span><span className="font-mono">{fmt(full.total_iva5)}</span>
            </div>
            <div className="flex justify-end gap-8">
              <span className="text-muted-foreground">Gravadas 10%</span><span className="font-mono">{fmt(full.total_iva10)}</span>
            </div>
            <div className="flex justify-end gap-8">
              <span className="text-muted-foreground">IVA</span><span className="font-mono">{fmt(full.total_iva)}</span>
            </div>
            <div className="flex justify-end gap-8 font-bold text-lg">
              <span>TOTAL</span><span className="font-mono text-primary">{fmt(full.total_general)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
